import { InvestorProfileCardStyles } from './InvestorProfileCard.styles.tsx';
import { Button, Flex, Text, Tooltip, useMantineTheme } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { InvestmentDetailsCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/InvestmentDetailsCard.tsx';
import { ContactItem } from '@dealstack/frontend/shared/components/ContactItem/ContactItem.tsx';
import { useNavigate } from 'react-router-dom';
import { investorDetailsPath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { InvestmentDetailsRowItem } from '../InvestmentDetailsRowItem/InvestmentDetailsRowItem.tsx';
import { UserInitialsBadge } from '@dealstack/frontend/shared/components/UserInitialsBadge/UserInitialsBadge.tsx';
import {
	DATE_FORMATS,
	formatStringDateToRelative,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date.ts';
import { getCountryNameByCode } from '@dealstack/shared/lib/countries/countriesData.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { PermissionRole, ResourceType } from '@dealstack/shared/lib/generated';
import React from 'react';
import { ManageRequirementsButton } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsCard/ManageRequirementsButton.tsx';

interface TaxResidence {
	countryCode: string;
	number: string | null;
}

interface Props {
	investorId: string;
	investorName: string;
	lastLoginAt: string | null;
	email: string | null;
	phone: string | null;
	placeOfBirth: string | null;
	countryOfBirth: string | null;
	isPlaceOfBirthRequired: boolean;
	isCountryOfBirthRequired: boolean;
	nationality: string[];
	domicile: string | null;
	taxResidences: TaxResidence[];
	onInvestorProfileClick: () => void;
	canMutate: boolean;
}

export const InvestorProfileCard = ({
	investorId,
	investorName,
	lastLoginAt,
	email,
	phone,
	placeOfBirth,
	countryOfBirth,
	isPlaceOfBirthRequired,
	isCountryOfBirthRequired,
	nationality,
	domicile,
	taxResidences,
	onInvestorProfileClick,
	canMutate,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const theme = useMantineTheme();
	const navigate = useNavigate();

	const investorNames = investorName.split(' ');

	const lastLoginItem = lastLoginAt ? (
		<Tooltip label={formatStringDateToUI(lastLoginAt, DATE_FORMATS.CALENDAR_DATE)}>
			<Text fz="sm" fw={400} c="dark.8">
				{formatStringDateToRelative(lastLoginAt)}
			</Text>
		</Tooltip>
	) : (
		<Text fz="sm" fw={400} c="dark.8">
			{tMipManage('investment_details_sidebar.investor_profile.last_seen_never')}
		</Text>
	);

	return (
		<InvestmentDetailsCard
			cardLabel={tMipManage('investor_profile_card.investor_profile.card_label')}
			actions={
				<>
					<PermissionWrapper
						resourceType={ResourceType.PROGRAMME}
						resourceId={mipId}
						permissionRole={PermissionRole.EDITOR}
					>
						<ManageRequirementsButton
							canMutate={canMutate}
							onClick={onInvestorProfileClick}
						>
							{tMipManage('investor_profile_card.investor_profile.manage')}
						</ManageRequirementsButton>
					</PermissionWrapper>

					<Button
						variant="default"
						rightSection={<Icon variant="arrow-up-right-2" size="sm" />}
						onClick={() =>
							navigate({
								pathname: investorDetailsPath({
									dealId,
									mipId,
									investorId,
								}),
							})
						}
					>
						{tMipManage('investor_profile_card.investor_profile.profile')}
					</Button>
				</>
			}
		>
			<InvestorProfileCardStyles>
				<Flex justify="space-between" align="center" gap="0.5rem" p="0 0.5rem" mb="0.5rem">
					<Flex gap="0.5rem" align="center" style={{ flex: 1, minWidth: 0 }}>
						<UserInitialsBadge
							firstName={investorNames[0]}
							lastName={investorNames[1]}
							size="xl"
						/>

						<Text
							fz="lg"
							fw={600}
							c="dark.8"
							title={investorName}
							className="investor-name"
						>
							{investorName}
						</Text>
					</Flex>

					<Flex gap="0.25rem" align="center" style={{ flexShrink: 0 }}>
						<Text fz="sm" fw={400} c="gray.9">
							{tMipManage('investor_profile.last_seen')}
						</Text>

						{lastLoginItem}
					</Flex>
				</Flex>

				<Flex
					gap="0.25rem"
					wrap="wrap"
					align="center"
					p="0 0.5rem"
					mb="0.5rem"
					style={{ minWidth: 0 }}
				>
					{email && (
						<ContactItem
							label={email}
							truncate
							icon={<Icon variant="mail" size="lg" color={theme.colors.gray[9]} />}
						/>
					)}

					{phone && (
						<ContactItem
							label={phone}
							truncate
							icon={<Icon variant="phone" size="lg" color={theme.colors.gray[9]} />}
						/>
					)}
				</Flex>

				{isPlaceOfBirthRequired && placeOfBirth && (
					<InvestmentDetailsRowItem
						truncate
						label={
							<Text fw={400} c="dark.0" style={{ flexShrink: 0 }}>
								{tMipManage('investor_data.place_of_birth.label')}
							</Text>
						}
						content={
							<Text fw={600} c="dark.8" title={placeOfBirth} className="pob">
								{placeOfBirth}
							</Text>
						}
					/>
				)}

				{isCountryOfBirthRequired && countryOfBirth && (
					<InvestmentDetailsRowItem
						label={
							<Text fw={400} c="dark.0">
								{tMipManage('investor_profile.country_of_birth')}
							</Text>
						}
						content={
							<Text fw={600} c="dark.8">
								{getCountryNameByCode(countryOfBirth) ?? countryOfBirth}
							</Text>
						}
					/>
				)}

				{!!nationality.length && (
					<InvestmentDetailsRowItem
						label={
							<Text fw={400} c="dark.0">
								{tMipManage('investor_profile.nationality')}
							</Text>
						}
						content={
							<Flex direction="column" align="flex-end" gap="0.25rem">
								{nationality.map((code) => (
									<Text key={code} fw={600} c="dark.8">
										{getCountryNameByCode(code) ?? code}
									</Text>
								))}
							</Flex>
						}
						align="flex-start"
					/>
				)}

				{domicile && (
					<InvestmentDetailsRowItem
						label={
							<Text fw={400} c="dark.0">
								{tMipManage('investor_profile.domicile')}
							</Text>
						}
						content={
							<Text fw={600} c="dark.8">
								{getCountryNameByCode(domicile) ?? domicile}
							</Text>
						}
					/>
				)}

				{!!taxResidences.length && (
					<InvestmentDetailsRowItem
						label={
							<Text fw={400} c="dark.0">
								{tMipManage('investor_profile.tax_residencies')}
							</Text>
						}
						content={
							<Flex direction="column" gap="0.5rem">
								{taxResidences.map((tr) => (
									<Flex
										key={`${tr.countryCode}-${tr.number ?? ''}`}
										direction="column"
										align="flex-end"
										gap="0.25rem"
									>
										<Text fw={600} c="dark.8">
											{getCountryNameByCode(tr.countryCode) ?? tr.countryCode}
										</Text>

										{tr.number && (
											<ContactItem
												label={tMipManage('investor_profile.tin', {
													tinNumber: tr.number,
												})}
												color="dark.0"
												fw={600}
												truncate
											/>
										)}
									</Flex>
								))}
							</Flex>
						}
						align="flex-start"
					/>
				)}
			</InvestorProfileCardStyles>
		</InvestmentDetailsCard>
	);
};
